import { reactive, getCurrentInstance } from 'vue'
import store from '@/store'
import { config } from '@/config.js'
import { useToast } from '@/composables/common/useToast'

/**
 * useAbonadoCards
 * Composable that centralizes all card-related state and business logic
 * for the Abonado Show view (impresos, invitaciones, documentos, asientos).
 *
 * @param {Ref} abonadoRef - ref to the abonado object
 * @param {Ref} propsRef - ref to the component props (abonado_id, lista_id)
 * @param {Function} onAbonadoUpdated - callback invoked after mutations that refresh state
 * @returns {Object} - public API with state and methods
 */
export function useAbonadoCards(abonadoRef, propsRef, onAbonadoUpdated = async() => {}) {
    const toast = useToast()
    const instance = getCurrentInstance()

    // ==================== STATE ====================

    /**
     * Centralized state for all 4 cards + modals
     */
    const state = reactive({
        impresos: { data: [], page: 1, pageSize: 10, total: 0, loading: false },
        invitaciones: { data: [], page: 1, pageSize: 10, total: 0, loading: false },
        documentos: { data: [], loading: false },
        asientos: { data: [], loading: false },
        // Track which tabs have been fetched at least once (lazy-once pattern)
        fetchedTabs: { impresos: false, invitaciones: false },
        modals: {
            editDocumento: false,
            selectedDocumento: null,
            confirmLiberar: false,
            selectedAsiento: null
        }
    })

    // ==================== HELPER FUNCTIONS ====================

    /**
     * Unified error notification
     */
    function notifyError(message) {
        if (toast && toast.error) {
            toast.error(message)
        } else if (instance?.proxy?.$toast) {
            instance.proxy.$toast.error(message)
        }
    }

    /**
     * Unified success notification
     */
    function notifySuccess(message) {
        if (toast && toast.success) {
            toast.success(message)
        } else if (instance?.proxy?.$toast) {
            instance.proxy.$toast.success(message)
        }
    }

    // ==================== FETCH METHODS (READ-ONLY) ====================

    /**
     * Fetch impresos (printed documents)
     */
    async function fetchImpresos(page = 1) {
        // Guard: ensure abonado is present
        if (!abonadoRef.value?.id && !propsRef.value?.abonado_id) {
            return
        }

        state.impresos.loading = true
        state.impresos.page = page

        const params = {
            abonado_id: abonadoRef.value?.id || propsRef.value.abonado_id,
            filters: {
                page: page,
                per_page: state.impresos.pageSize
            }
        }

        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.abonados.abonado.impresion'),
                params
            )
            if (res.data && res.data.data) {
                state.impresos.data = res.data.data
                state.impresos.total = res.data.meta?.total || 0
                // Mark impresos as fetched (lazy-once pattern)
                state.fetchedTabs.impresos = true
            }
        } catch (error) {
            console.error('Error fetching impresos:', error)
            notifyError('Error al cargar los impresos')
        } finally {
            state.impresos.loading = false
        }
    }

    /**
     * Fetch invitaciones (invitations)
     */
    async function fetchInvitaciones(page = 1) {
        // Guard: ensure abonado is present
        if (!abonadoRef.value?.id && !propsRef.value?.abonado_id) {
            return
        }

        state.invitaciones.loading = true
        state.invitaciones.page = page

        const params = {
            abonado_id: abonadoRef.value?.id || propsRef.value.abonado_id,
            filters: {
                page: page
            }
        }

        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.abonados.abono.listado-invitaciones-eventos'),
                params
            )

            if (res.data && !res.data.error) {
                state.invitaciones.data = res.data.data || []
                state.invitaciones.total = res.data.meta?.total || 0
                state.invitaciones.pageSize = res.data.meta?.per_page || 10
                // Mark invitaciones as fetched (lazy-once pattern)
                state.fetchedTabs.invitaciones = true
            }
        } catch (error) {
            if (error.response?.status === 403) {
                state.invitaciones.data = []
                state.invitaciones.total = 0
                state.invitaciones.pageSize = 10
                state.fetchedTabs.invitaciones = true
                return
            }

            console.error('Error fetching invitaciones:', error)
            notifyError('Error al cargar las invitaciones')
        } finally {
            state.invitaciones.loading = false
        }
    }

    /**
     * Fetch documentos (from abonado.documentos, no API call needed)
     */
    async function fetchDocumentos() {
        // Guard: ensure abonado is present
        if (!abonadoRef.value?.id && !propsRef.value?.abonado_id) {
            return
        }

        state.documentos.loading = true
        // Documentos come from abonado.documentos, no separate API call
        if (abonadoRef.value && abonadoRef.value.documentos) {
            state.documentos.data = abonadoRef.value.documentos
        }
        state.documentos.loading = false
    }

    /**
     * Fetch asientos liberados (from abonado.asientos_liberados, no API call needed)
     */
    async function fetchAsientos() {
        // Guard: ensure abonado is present
        if (!abonadoRef.value?.id && !propsRef.value?.abonado_id) {
            return
        }

        state.asientos.loading = true
        // Asientos come from abonado.asientos_liberados
        if (abonadoRef.value && abonadoRef.value.asientos_liberados) {
            state.asientos.data = abonadoRef.value.asientos_liberados
        }
        state.asientos.loading = false
    }

    // ==================== MUTATION METHODS (WITH SIDE EFFECTS) ====================

    /**
     * Liberar asiento (recover freed seat)
     * Calls onAbonadoUpdated callback after success to refresh parent state
     */
    async function liberarAsiento(asientoId) {
        // Guard: ensure abonado is present
        if (!abonadoRef.value?.id && !propsRef.value?.abonado_id) {
            return
        }

        const params = {
            abonado_id: abonadoRef.value?.id || propsRef.value.abonado_id,
            asiento_liberado_id: asientoId
        }

        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.abonados.asientos_liberados.recuperar'),
                params
            )
            notifySuccess(res.data.message || 'Asiento recuperado correctamente')
            // Invoke callback to refresh parent state and cards
            await onAbonadoUpdated()
            await fetchAsientos()
        } catch (error) {
            console.error('Error recuperando asiento:', error)
            notifyError(error.response?.data?.message || 'Error al recuperar el asiento')
        }
    }

    /**
     * Delete documento
     * Calls onAbonadoUpdated callback after success to refresh parent state
     */
    async function deleteDocumento(documentoId) {
        // Guard: ensure abonado is present
        if (!abonadoRef.value?.id && !propsRef.value?.abonado_id) {
            return
        }

        const params = {
            abonado_id: abonadoRef.value?.id || propsRef.value.abonado_id,
            documento_id: documentoId,
            lista_id: propsRef.value.lista_id
        }

        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.abonados.borrar.documento'),
                params
            )
            notifySuccess(res.data?.message || 'Documento eliminado')
            // Invoke callback to refresh parent state and cards
            await onAbonadoUpdated()
            await fetchDocumentos()
        } catch (error) {
            console.error('Error deleting documento:', error)
            notifyError(error.response?.data?.message || 'El archivo que intentas borrar no existe')
        }
    }

    /**
     * Update documento
     * Calls onAbonadoUpdated callback after success to refresh parent state
     */
    async function updateDocumento(documento) {
        // Guard: ensure abonado is present
        if (!abonadoRef.value?.id && !propsRef.value?.abonado_id) {
            return
        }

        const datos = {
            abonado_id: abonadoRef.value?.id || propsRef.value.abonado_id,
            documento_id: documento.id,
            documento: documento,
            filters: {}
        }

        try {
            await store.dispatch(
                config.getVuexAlias('back.abonados.editar.documento'),
                datos
            )
            notifySuccess('Documento editado correctamente')
            // Invoke callback to refresh parent state and cards
            await onAbonadoUpdated()
            await fetchDocumentos()
        } catch (error) {
            console.error('Error updating documento:', error)
            notifyError('Ha ocurrido un error al editar el documento')
        }
    }

    // ==================== MODAL HELPERS ====================

    /**
     * Open modal by name
     * Only manipulates local state.modals; Show.vue handles $modal.show()
     */
    function openModal(name, payload = null) {
        if (name === 'editDocumento') {
            state.modals.editDocumento = true
            state.modals.selectedDocumento = payload
        } else if (name === 'confirmLiberar') {
            state.modals.confirmLiberar = true
            state.modals.selectedAsiento = payload
        }
    }

    /**
     * Close modal by name
     * Only manipulates local state.modals; Show.vue handles $modal.hide()
     */
    function closeModal(name) {
        if (name === 'editDocumento') {
            state.modals.editDocumento = false
            state.modals.selectedDocumento = null
        } else if (name === 'confirmLiberar') {
            state.modals.confirmLiberar = false
            state.modals.selectedAsiento = null
        }
    }

    // ==================== PUBLIC API ====================
    return {
        // State
        state,

        // Read-only fetch methods
        fetchImpresos,
        fetchInvitaciones,
        fetchDocumentos,
        fetchAsientos,

        // Mutation methods
        liberarAsiento,
        deleteDocumento,
        updateDocumento,

        // Modal helpers
        openModal,
        closeModal
    }
}
